import { useCallback, useEffect, useRef, useState } from 'react';

import { useDebounce } from 'use-debounce';

import { useGoogleMapsPlaces } from './useGoogleMapsPlaces';

export interface AutocompleteSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text?: string;
    main_text_matched_substrings: Array<{ offset: number; length: number }>;
  };
  types: string[];
}

interface UseGoogleMapsAutocompleteOptions {
  requestOptions?: { types?: string[] };
  debounce?: number;
}

const convertSuggestion = (suggestion: any, index: number): AutocompleteSuggestion => {
  if (suggestion.placePrediction) {
    const { placePrediction } = suggestion;
    const fullText = placePrediction.text.text;

    const parts = fullText.split(', ');
    const main_text = parts[0];
    const secondary_text = parts.slice(1).join(', ');

    return {
      place_id: placePrediction.placeId,
      description: fullText,
      structured_formatting: {
        main_text,
        secondary_text,
        main_text_matched_substrings: placePrediction.text.matches || []
      },
      types: []
    };
  }

  if (suggestion.queryPrediction) {
    const { queryPrediction } = suggestion;
    const fullText = queryPrediction.text.text;

    return {
      place_id: `query_${Date.now()}_${index}`,
      description: fullText,
      structured_formatting: {
        main_text: fullText,
        secondary_text: '',
        main_text_matched_substrings: queryPrediction.text.matches || []
      },
      types: []
    };
  }

  return {
    place_id: `fallback_${Date.now()}_${index}`,
    description: 'Location not found',
    structured_formatting: {
      main_text: 'Location not found',
      secondary_text: '',
      main_text_matched_substrings: []
    },
    types: []
  };
};

const initializePlacesAPI = async () => {
  if (!window.google?.maps?.importLibrary) throw new Error('Google Maps not available');
  const placesLib = await window.google.maps.importLibrary('places');
  const hasNewAPI = !!(placesLib && 'AutocompleteSuggestion' in placesLib);
  return { placesLib, hasNewAPI };
};

const fetchSuggestions = async (placesLib: any, input: string, types: string[]) => {
  const result = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    includedPrimaryTypes: types
  });

  const converted = result?.suggestions?.map(convertSuggestion) || [];
  return converted;
};

const getErrorMessage = (error: any) => {
  return error.message?.includes('Places API (New) has not been used')
    ? 'Places API (New) needs to be enabled in Google Cloud Console.'
    : 'Error fetching place suggestions with new API.';
};

export const useGoogleMapsAutocomplete = (options: UseGoogleMapsAutocompleteOptions = {}) => {
  const { isReady } = useGoogleMapsPlaces();
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiReady, setApiReady] = useState(false);

  const placesLibRef = useRef<any>(null);
  const isSelectionRef = useRef(false);
  const typesRef = useRef(options.requestOptions?.types || []);

  const [debouncedValue] = useDebounce(value, options.debounce || 200);

  useEffect(() => {
    if (!isReady || apiReady) return;

    initializePlacesAPI()
      .then(({ placesLib, hasNewAPI }) => {
        placesLibRef.current = placesLib;
        setApiReady(hasNewAPI);
      })
      .catch(() => {
        setError('Places API (New) needs to be enabled in Google Cloud Console.');
        setApiReady(false);
      });
  }, [isReady, apiReady]);

  useEffect(() => {
    typesRef.current = options.requestOptions?.types || [];
  }, [options.requestOptions?.types]);

  useEffect(() => {
    const shouldSkipSearch = isSelectionRef.current || !debouncedValue.trim() || !apiReady;

    if (isSelectionRef.current) {
      isSelectionRef.current = false;
    }

    if (shouldSkipSearch) {
      setSuggestions([]);
      return;
    }

    const search = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await fetchSuggestions(placesLibRef.current, debouncedValue, typesRef.current);
        setSuggestions(results);
      } catch (error: any) {
        setError(getErrorMessage(error));
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedValue, apiReady]);

  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  const setValueFromSelection = useCallback((selectedValue: string) => {
    isSelectionRef.current = true;
    setValue(selectedValue);
    setSuggestions([]);
  }, []);

  return {
    ready: isReady && apiReady,
    value,
    setValue,
    setValueFromSelection,
    suggestions: {
      status: suggestions.length > 0 ? 'OK' : 'ZERO_RESULTS',
      data: suggestions,
      loading: isLoading
    },
    clearSuggestions,
    error
  };
};
